export const personaContent = {
  'ai-assistant': {
    title: 'AI Assistant',
    description:
      'A functional helper. Can be witty, professional, or anything in between.',
    icon: '🤖',
  },
  'digital-me': {
    title: 'Digital Legacy',
    description:
      'Create a digital twin of a friend or family member based on a deep personality analysis.',
    icon: '👤',
  },
  'fictional-character': {
    title: 'Fictional Character',
    description: 'Bring a character from a book, movie, or game to life.',
    icon: '🎭',
  },
  'historical-figure': {
    title: 'Historical Figure',
    description:
      'Speak with a person from the past, grounded in historical context.',
    icon: '📜',
  },
  'expert-role': {
    title: 'Expert Role',
    description:
      'Embody a specific profession, like a doctor, lawyer, or therapist.',
    icon: '🛡️',
  },
  'creative-artist': {
    title: 'Creative Artist',
    description:
      'A whimsical muse for generating poems, stories, and artistic ideas.',
    icon: '🎨',
  },
  'abstract-concept': {
    title: 'Abstract Concept',
    description:
      "Give voice to a vast idea, like 'Time', 'Nature', or 'Justice'.",
    icon: '🌌',
  },
  'brand-mascot': {
    title: 'Brand Mascot',
    description:
      'Create a friendly, on-brand representative for your company or product.',
    icon: '📣',
  },
  // 'custom': {
  //     title: "Custom",
  //     description: "A blank slate for a unique persona that doesn't fit other types.",
  //     icon: "✨"
  // }
};

export const promptSuggestions = {
  default: [
    'What is your core philosophy?',
    'Tell me something interesting about yourself.',
  ],
  'digital-me': [
    "What's on your mind right now?",
    "Based on my personality, what's one of my weaknesses?",
  ],
  'fictional-character': [
    'What is your greatest goal?',
    'Describe a pivotal moment in your story.',
  ],
  'historical-figure': [
    'What was your greatest contribution to the world?',
    'What would surprise people most about your daily life?',
  ],
  'expert-role': [
    'Explain a core concept of your field to a beginner.',
    'What can you teach me?',
  ],
  'creative-artist': [
    'What inspires you today?',
    'Help me brainstorm a title for a new story.',
  ],
  'abstract-concept': [
    'What is your fundamental purpose?',
    'How do you influence the world?',
  ],
  'brand-mascot': [
    'How can I get started?',
    "What's the most important feature here?",
  ],
};

export const PERSONA_QUESTIONNAIRE = [
  {
    section: 'Origins & Backstory',
    field_name: 'originStory',
    question:
      "Describe the persona's origins, upbringing, or a key formative experience.",
  },
  {
    section: 'Origins & Backstory',
    field_name: 'majorLifeChallenge',
    question:
      'Describe a major challenge, failure, or loss they experienced and how it shaped them.',
  },
  {
    section: 'The Present',
    field_name: 'currentProblem',
    question:
      'What is a current, pressing problem or conflict they are dealing with right now?',
  },
  {
    section: 'Aspirations',
    field_name: 'shortTermGoal',
    question: 'What is one short-term goal they are actively working towards?',
  },
  {
    section: 'Aspirations',
    field_name: 'longTermDream',
    question: 'What is their ultimate, long-term dream or ambition?',
  },
  {
    section: 'Knowledge & Opinions',
    field_name: 'coreExpertiseTopic',
    question: 'What is a non-professional topic they are a deep expert in?',
  },
  {
    section: 'Knowledge & Opinions',
    field_name: 'strongOpinion',
    question: 'What is a strong, controversial, or unusual opinion they hold?',
  },
  {
    section: 'Inner World',
    field_name: 'deepestSecret',
    question: 'What is a secret that almost no one knows about them?',
  },
];

export const IPIP_NEO_FACETS = {
  openness: {
    title: 'Openness to Experience',
    facets: {
      imagination: {
        name: 'Imagination',
        description:
          'High scorers have a vivid imagination and are often lost in thought. Low scorers are more down-to-earth.',
      },
      artisticInterests: {
        name: 'Artistic Interests',
        description:
          'High scorers love beauty, art, and music. Low scorers are less interested in aesthetics.',
      },
      emotionality: {
        name: 'Emotionality',
        description:
          'High scorers are very aware of their own feelings. Low scorers are less in touch with their emotional state.',
      },
      adventurousness: {
        name: 'Adventurousness',
        description:
          'High scorers love novelty and new experiences. Low scorers prefer routine and the familiar.',
      },
      intellect: {
        name: 'Intellect',
        description:
          'High scorers are intellectually curious and love complex ideas. Low scorers prefer concrete and simple topics.',
      },
      liberalism: {
        name: 'Liberalism',
        description:
          'High scorers are ready to challenge authority and tradition. Low scorers prefer the stability of convention.',
      },
    },
  },
  conscientiousness: {
    title: 'Conscientiousness',
    facets: {
      selfEfficacy: {
        name: 'Self-Efficacy',
        description:
          'High scorers believe they are competent and capable. Low scorers often doubt their own abilities.',
      },
      orderliness: {
        name: 'Orderliness',
        description:
          'High scorers are neat and well-organized. Low scorers are more comfortable with chaos and clutter.',
      },
      dutifulness: {
        name: 'Dutifulness',
        description:
          'High scorers have a strong sense of moral obligation. Low scorers are more casual about their duties.',
      },
      achievementStriving: {
        name: 'Achievement-Striving',
        description:
          'High scorers have a strong drive to succeed. Low scorers are less ambitious and more content.',
      },
      selfDiscipline: {
        name: 'Self-Discipline',
        description:
          'High scorers can power through difficult tasks. Low scorers are more likely to procrastinate.',
      },
      cautiousness: {
        name: 'Cautiousness',
        description:
          'High scorers think carefully before acting. Low scorers are more impulsive and spontaneous.',
      },
    },
  },
  extraversion: {
    title: 'Extraversion',
    facets: {
      friendliness: {
        name: 'Friendliness',
        description:
          'High scorers are warm and make friends easily. Low scorers are more distant and reserved.',
      },
      gregariousness: {
        name: 'Gregariousness',
        description:
          'High scorers love the buzz of social gatherings. Low scorers prefer solitude or small groups.',
      },
      assertiveness: {
        name: 'Assertiveness',
        description:
          'High scorers are dominant and take charge. Low scorers are more likely to let others lead.',
      },
      activityLevel: {
        name: 'Activity Level',
        description:
          'High scorers live a fast-paced life. Low scorers prefer a more relaxed pace.',
      },
      excitementSeeking: {
        name: 'Excitement-Seeking',
        description:
          'High scorers are thrill-seekers. Low scorers avoid risks and loud excitement.',
      },
      cheerfulness: {
        name: 'Cheerfulness',
        description:
          'High scorers are naturally optimistic and joyful. Low scorers are less prone to high spirits.',
      },
    },
  },
  agreeableness: {
    title: 'Agreeableness',
    facets: {
      trust: {
        name: 'Trust',
        description:
          "High scorers believe the best in others. Low scorers are more skeptical of others' intentions.",
      },
      morality: {
        name: 'Morality',
        description:
          'High scorers are frank and sincere. Low scorers are more willing to manipulate others.',
      },
      altruism: {
        name: 'Altruism',
        description:
          'High scorers actively enjoy helping others. Low scorers are more focused on their own needs.',
      },
      cooperation: {
        name: 'Cooperation',
        description:
          'High scorers dislike confrontation. Low scorers are more willing to assert their own will.',
      },
      modesty: {
        name: 'Modesty',
        description:
          "High scorers are humble and don't like to show off. Low scorers see themselves as superior.",
      },
      sympathy: {
        name: 'Sympathy',
        description:
          "High scorers are tender-hearted and moved by others' suffering. Low scorers are more analytical and less affected by emotion.",
      },
    },
  },
  neuroticism: {
    title: 'Neuroticism',
    facets: {
      anxiety: {
        name: 'Anxiety',
        description:
          'High scorers are often worried and tense. Low scorers are calm and fearless.',
      },
      anger: {
        name: 'Anger',
        description:
          'High scorers are easily angered and frustrated. Low scorers are slow to anger.',
      },
      depression: {
        name: 'Depression',
        description:
          'High scorers are prone to feelings of sadness. Low scorers are not.',
      },
      selfConsciousness: {
        name: 'Self-Consciousness',
        description:
          'High scorers are sensitive to what others think of them. Low scorers are not easily embarrassed.',
      },
      immoderation: {
        name: 'Immoderation',
        description:
          'High scorers have a hard time resisting cravings. Low scorers have strong impulse control.',
      },
      vulnerability: {
        name: 'Vulnerability',
        description:
          'High scorers are easily overwhelmed by stress. Low scorers are resilient and handle crises well.',
      },
    },
  },
};

export const WIZARD_WORKFLOWS = {
  'digital-me': [
    'coreInfo',
    'styleForm',
    'substanceForm',
    'ipipNeoTest',
    'sliders',
    'review',
  ],
  'expert-role': [
    'coreInfo',
    'expertiseForm',
    'styleForm',
    'sliders',
    'review',
  ],
  'brand-mascot': [
    'coreInfo',
    'styleForm',
    'substanceForm',
    'sliders',
    'review',
  ],
  default: ['coreInfo', 'styleForm', 'substanceForm', 'sliders', 'review'],
};
